import { AnyAction } from '@reduxjs/toolkit'
import { get } from 'lodash'

type RequestState = {
  [key: string]: any
}

const initialState: RequestState = {}

/**
 * Maintains metadata about network requests. The lifecycle of each request is maintained to determine if a request is
 * pending, what error it may have failed with, and if it has completed successfully. Isolating this metadata globally
 * in this reducer allows canonical Redux state to not become polluted with flags like `isFetching`.
 */
export default function requestReducer(state = initialState, action: AnyAction): RequestState {
  const parsedAction = /(.*)_(CACHED|ERROR|REQUEST|SUCCESS|RESET)/.exec(action.type)

  if (!parsedAction) {
    return state
  }

  const [, request, status] = parsedAction
  let requestState = { ...get(state, request, {}) }

  switch (status) {
    case 'REQUEST': {
      if (action.shouldNotClearError) {
        // Some requests are not retries, they are actually the same network call but need all of them to succeed
        // This can be seen in the CSV "upload" for Publisher/Survey audience targeting
        requestState = { ...requestState, isPending: true, isSuccess: false }
      } else {
        requestState = { error: null, isPending: true, isSuccess: false }
      }

      break
    }
    case 'ERROR': {
      requestState = {
        error: {
          code: get(action, 'error.data.code') || get(action, 'error.code') || 'UNK-UNCOMMON',
          message: get(action, 'error.data.message') || get(action, 'error.message') || 'Request failed',
          httpStatusCode: get(action, 'error.code')
        },
        isPending: false,
        // Why is this true when there's an error? This is a global change so we should leave it for now
        isSuccess: true
      }

      break
    }
    case 'CACHED':
    case 'SUCCESS': {
      if (action.shouldNotClearError) {
        // Some requests are not retries, they are actally the same network call but need all of them to succeed
        // This can be seen in the CSV "upload" for Publisher/Survey audience targeting
        requestState = { ...requestState, isPending: false, isSuccess: !requestState.error }
      } else {
        requestState = { error: null, isPending: false, isSuccess: true }
      }

      break
    }
    case 'RESET': {
      requestState = { error: null, isPending: false, isSuccess: false }

      break
    }
    // No default
  }

  return { ...state, [request]: requestState }
}
